import useUrlQueryStore from '@shared/useUrlQueryStore';
import {OptionalTerms} from '@di-shared/api/di/__generated';
import {useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';

export enum MileageAgreementStatus {
  INTEGRATION_AVAILABLE, // = "연동가능",
  UNSUPPORTED_VEHICLE, // = "미지원 차량",
  MEMBERSHIP_REQUIRED, // = "회원가입 필요",
  VEHICLE_REGISTRATION_REQUIRED, // = "차량등록 필요",
  INTEGRATED_MEMBERSHIP_CONVERSION_REQUIRED, // = "통합회원전환 필요",
}

interface UseMileageAgreementProps {
  terms: (OptionalTerms & {isRequired: boolean; isRequiredText: boolean})[];
}

function getMileageAgreementStatus({
  mberYn,
  confirmYn,
  carConYn,
  carMberYn,
}: {
  mberYn: string;
  confirmYn: string;
  carConYn: string;
  carMberYn: string;
}) {
  if (mberYn === '02') {
    return MileageAgreementStatus.MEMBERSHIP_REQUIRED;
  }
  if (confirmYn === '02') {
    return MileageAgreementStatus.INTEGRATED_MEMBERSHIP_CONVERSION_REQUIRED;
  }
  if (carConYn === '02') {
    return MileageAgreementStatus.UNSUPPORTED_VEHICLE;
  }
  if (carMberYn === '02') {
    return MileageAgreementStatus.VEHICLE_REGISTRATION_REQUIRED;
  }
  return MileageAgreementStatus.INTEGRATION_AVAILABLE;
}

type AgreementParam = {
  [key: string]: boolean;
};

function useMileageAgreement({terms}: UseMileageAgreementProps) {
  const [searchParams] = useSearchParams();
  const [agreements, setAgreements] = useUrlQueryStore<AgreementParam>(
    'agreements',
    terms.reduce(
      (acc, term) => ({
        ...acc,
        [term.termsId]: false,
      }),
      {}
    )
  );
  useEffect(() => {
    if (!searchParams.size) {
      selectAllAgreements(false);
    }
  }, []);

  function changeAgreement(termsId: string) {
    setAgreements((prev) => ({...prev, [termsId]: !prev[termsId]}), true);
  }

  function agreeTerms(termsId: string) {
    setAgreements((prev) => ({...prev, [termsId]: true}), true);
  }

  const selectAllAgreements = (isChecked: boolean) => {
    setAgreements((prev) => {
      const newAgreements = {...prev};
      Object.keys(newAgreements).forEach((termId) => {
        newAgreements[termId] = isChecked;
      });
      return newAgreements;
    }, true);
  };

  const isAllAgreed = Object.values(agreements).every((value) => value);

  const isAllMandatoryAgreed = terms.filter((term) => term.isRequired).every((term) => agreements[term.termsId]);

  return {
    getMileageAgreementStatus,
    agreements,
    changeAgreement,
    agreeTerms,
    isAllMandatoryAgreed,
    selectAllAgreements,
    isAllAgreed,
  };
}

export default useMileageAgreement;
